import { useMemo } from 'react'
import { permissionCheck } from '../../baseComponents/PermissionChecker/function'
import { haveModuleBuyed } from '../globals'
import { getConfigParam, getModulesBuyed, getUserPermissions } from '../reducers/getters'
import useTwinTranslation from './useTwinTranslation'
import { ShortCutActions } from '../../baseComponents/AppLayout/LeftMenu/IconMenuBar/UserIcons/ShortcutIcons/types'
import { TwinDictionary } from '../globals/dictionary'

export interface ActionItem {
    name: string
    havePermission?: boolean
    haveModuleBuyed?: boolean
}
const useShortcuts = () => {
    const { t } = useTwinTranslation()
    const navigate = t('navigate', 'Navegar a')
    const createCustomer = t('createCustomer', 'Crear cliente')
    const listingAllCustomers = t('listingAllCustomers', 'Mostrar todos los clientes')
    const createCustomerB2B = t('createCustomerB2B', 'Crear cliente B2B')
    const listingCustomersB2B = t('listingCustomersB2B', 'Mostrar clientes B2B')
    const createCustomerB2C = t('createCustomerB2C', 'Crear cliente B2C')
    const listingCustomersB2C = t('listingCustomersB2C', 'Mostrar clientes B2C')
    const createProduct = t('createProduct', 'Crear producto')
    const listingProducts = t('listingProducts', 'Mostrar productos')
    const createPackaging = t('createPackaging', 'Crear packaging')
    const listingPackagings = t('listingPackagings', 'Mostrar packagings')
    const createService = t('createService', 'Crear servicio')
    const listingServices = t('listingServices', 'Mostrar servicios')
    const createBagOfHours = t('createBagOfHours', 'Crear bolsa de horas')
    const createHolidayRequest = t('createHolidayRequest', 'Crear solicitud de vacaciones')
    const createEmployeeAbsence = t('createEmployeeAbsence', 'Crear ausencia')
    const createOvertimeRequest = t('createOvertimeRequest', 'Crear horas extras')
    const createExpensesRequest = t('createExpensesRequest', 'Crear gasto')
    const createMaterial = t('createMaterial', 'Crear material')
    const listingMaterials = t('listingMaterials', 'Mostrar materiales')
    const createQuoteOrder = t('createQuoteOrder', 'Crear presupuesto')
    const listingQuoteOrders = t('listingQuoteOrders', 'Mostrar presupuestos')
    const createAddon = t('createAddon', 'Crear complemento')
    const listingAddons = t('listingAddons', 'Mostrar complementos')
    const createTask = t('createTask', 'Crear tarea')
    const createTaskHelper = t('createTaskHelper', 'Crear tarea con ayudante')
    const filteredShortcuts = useMemo(() => {
        const showAllCustomersTogether = getConfigParam('showAllCustomersTogether') === 'on' ? true : false
        const permissions = {
            customerPermission: getUserPermissions('customer.profile.main'),
            customerPermissionRead: permissionCheck(getUserPermissions('customer.profile.main'), 'read'),
            productPermission: getUserPermissions('product.profile.main'),
            servicePermission: getUserPermissions('product.service.main'),
            packagingPermission: getUserPermissions('product.packaging.main'),
            bagOfHoursPermission: getUserPermissions('task.bagOfHours'),
            holidayRequestPermission: getUserPermissions('WCal.holiday.usedDays'),
            employeeAbsence: getUserPermissions('WCal.absences.control'),
            overtimeRequestPermission: getUserPermissions('WCal.overtime'),
            expensesRequest: getUserPermissions('expenses.main'),
            materialPermission: getUserPermissions('product.material.main'),
            quoteOrderPermission: getUserPermissions('quote.order'),
            addonPermission: getUserPermissions('product.addon.main'),
            taskPermission: getUserPermissions('task.main')
        }
        const myModules = getModulesBuyed()
        const modules = {
            tasks: haveModuleBuyed(["tasks"], myModules),
            products: haveModuleBuyed(["products"], myModules),
            wCal: haveModuleBuyed(['working_calendar_full'], myModules),
            quotes: haveModuleBuyed(["quotes"], myModules),
            holiday: haveModuleBuyed(["working_calendar_medium", "working_calendar_full"], myModules),
            expensedRequest: haveModuleBuyed(["employee_expenses"], myModules)
        }

        const shortcuts: Record<ShortCutActions, ActionItem> = {
            'navigate': {
                name: navigate,
                havePermission: true,
                haveModuleBuyed: true
            },
            'createCustomer': {
                name: createCustomer,
                havePermission: permissionCheck(permissions.customerPermission, 'create'),
                haveModuleBuyed: modules.tasks
            },
            'listingAllCustomers': {
                name: listingAllCustomers,
                havePermission: permissions.customerPermissionRead && showAllCustomersTogether,
                haveModuleBuyed: modules.tasks
            },
            'createCustomerB2B': {
                name: createCustomerB2B,
                havePermission: permissionCheck(permissions.customerPermission, 'create'),
                haveModuleBuyed: modules.tasks
            },
            'listingCustomersB2B': {
                name: listingCustomersB2B,
                havePermission: permissions.customerPermissionRead && !showAllCustomersTogether,
                haveModuleBuyed: modules.tasks
            },
            'createCustomerB2C': {
                name: createCustomerB2C,
                havePermission: permissionCheck(permissions.customerPermission, 'create'),
                haveModuleBuyed: modules.tasks
            },
            'listingCustomersB2C': {
                name: listingCustomersB2C,
                havePermission: permissions.customerPermissionRead && !showAllCustomersTogether,
                haveModuleBuyed: modules.tasks
            },
            'createProduct': {
                name: createProduct,
                havePermission: permissionCheck(permissions.productPermission, 'create'),
                haveModuleBuyed: modules.products
            },
            'listingProducts': {
                name: listingProducts,
                havePermission: permissionCheck(permissions.productPermission, 'read'),
                haveModuleBuyed: modules.products
            },
            'createPackaging': {
                name: createPackaging,
                havePermission: permissionCheck(permissions.packagingPermission, 'create'),
                haveModuleBuyed: modules.products
            },
            'listingPackagings': {
                name: listingPackagings,
                havePermission: permissionCheck(permissions.packagingPermission, 'read'),
                haveModuleBuyed: modules.products
            },
            'createService': {
                name: createService,
                havePermission: permissionCheck(permissions.servicePermission, 'create'),
                haveModuleBuyed: modules.products
            },
            'listingServices': {
                name: listingServices,
                havePermission: permissionCheck(permissions.servicePermission, 'read'),
                haveModuleBuyed: modules.products
            },
            'createBagOfHours': {
                name: createBagOfHours,
                havePermission: permissionCheck(permissions.bagOfHoursPermission, 'create'),
                haveModuleBuyed: modules.tasks
            },
            'createHolidayRequest': {
                name: createHolidayRequest,
                havePermission: permissionCheck(permissions.holidayRequestPermission, 'create'),
                haveModuleBuyed: modules.holiday
            },
            'createEmployeeAbsence': {
                name: createEmployeeAbsence,
                havePermission: permissionCheck(permissions.employeeAbsence, 'create'),
                haveModuleBuyed: modules.wCal
            },
            'createOvertimeRequest': {
                name: createOvertimeRequest,
                havePermission: permissionCheck(permissions.overtimeRequestPermission, 'create'),
                haveModuleBuyed: modules.wCal
            },
            'createExpensesRequest': {
                name: createExpensesRequest,
                havePermission: permissionCheck(permissions.expensesRequest, 'create'),
                haveModuleBuyed: modules.expensedRequest
            },
            'createMaterial': {
                name: createMaterial,
                havePermission: permissionCheck(permissions.materialPermission, 'create'),
                haveModuleBuyed: modules.products
            },
            'listingMaterials': {
                name: listingMaterials,
                havePermission: permissionCheck(permissions.materialPermission, 'read'),
                haveModuleBuyed: modules.products
            },
            'createQuoteOrder': {
                name: createQuoteOrder,
                havePermission: permissionCheck(permissions.quoteOrderPermission, 'create'),
                haveModuleBuyed: modules.quotes
            },
            'listingQuoteOrders': {
                name: listingQuoteOrders,
                havePermission: permissionCheck(permissions.quoteOrderPermission, 'read'),
                haveModuleBuyed: modules.quotes
            },
            'createAddon': {
                name: createAddon,
                havePermission: permissionCheck(permissions.addonPermission, 'create'),
                haveModuleBuyed: modules.products
            },
            'listingAddons': {
                name: listingAddons,
                havePermission: permissionCheck(permissions.addonPermission, 'read'),
                haveModuleBuyed: modules.products
            },
            'createTask': {
                name: createTask,
                havePermission: permissionCheck(permissions.taskPermission, 'create'),
                haveModuleBuyed: modules.tasks
            },
            'createTaskHelper': {
                name: createTaskHelper,
                havePermission: permissionCheck(permissions.taskPermission, 'create'),
                haveModuleBuyed: modules.tasks
            },
        }

        const myFilteredShortcuts: TwinDictionary = {}
        for (const key in shortcuts) {
            const element = shortcuts[key as ShortCutActions]
            if (element.havePermission && element.haveModuleBuyed) {
                myFilteredShortcuts[key] = element
            }
        }
        return myFilteredShortcuts
    }, [navigate, createCustomer, listingAllCustomers, createCustomerB2B, listingCustomersB2B, createCustomerB2C, listingCustomersB2C, createProduct, listingProducts, createPackaging, listingPackagings, createService, listingServices, createBagOfHours, createHolidayRequest, createEmployeeAbsence, createOvertimeRequest, createExpensesRequest, createMaterial, listingMaterials, createQuoteOrder, listingQuoteOrders, createAddon, listingAddons, createTask, createTaskHelper])
    
    return { shortcuts: filteredShortcuts }
}

export default useShortcuts